import { useTranslation } from 'react-i18next';
import './platform-anim.css';

/**
 * PlatformShowcase — zwei rein-CSS-animierte Produkt-Demos, portiert aus der
 * bunker.ai Sales-Deck. Die Animationen werden vollständig von `platform-anim.css`
 * gesteuert (Klassennamen 1:1 aus dem Deck). Alles ist unter der Root-Klasse
 * `.bnk-deck` gescoped — die dunklen "Produkt-Screens" tragen ihr eigenes
 * Styling und leaken nicht in die helle Homepage.
 *
 * Die frühere Chat-Demo wurde entfernt: die interaktive Plattform-Demo zeigt
 * die Wissenssuche bereits. Hier bleiben die zwei Visuals, die das *Wie*
 * erklären — die Wissenspyramide (HiRAG) als Held und die Antwort-Pipeline.
 */

/** Wissenspyramide (16s Loop) — angereichert um Ebenen-Labels, Fluss-Captions und Legende. */
function KnowledgePyramid() {
  const { t } = useTranslation('demo');
  return (
    <div className="anim-stage anim-pyramid" aria-label={t('deck.pyramidAriaLabel')}>
      {/* Fluss-Captions: Dokumente -> KI-Verarbeitung -> Wissenspyramide -> präzise Antwort */}
      <div className="ap-flowmap" aria-hidden="true">
        <span className="ap-flowstep">{t('deck.flowStep1')}</span>
        <span className="ap-flowarrow">&rarr;</span>
        <span className="ap-flowstep">{t('deck.flowStep2')}</span>
        <span className="ap-flowarrow">&rarr;</span>
        <span className="ap-flowstep">{t('deck.flowStep3')}</span>
        <span className="ap-flowarrow">&rarr;</span>
        <span className="ap-flowstep ap-flowstep-end">{t('deck.flowStep4')}</span>
      </div>

      <div className="ap-label" style={{ left: '4%', top: '14%' }}>
        {t('deck.pyramidLabelDocuments')}
      </div>
      <div className="ap-files">
        <span className="ap-file pdf">
          <i />
          {t('deck.pyramidFilePdf')}
        </span>
        <span className="ap-file doc">
          <i />
          {t('deck.pyramidFileWord')}
        </span>
        <span className="ap-file xls">
          <i />
          {t('deck.pyramidFileExcel')}
        </span>
        <span className="ap-file scan">
          <i />
          {t('deck.pyramidFileScan')}
        </span>
        <span className="ap-file img">
          <i />
          {t('deck.pyramidFilePhoto')}
        </span>
        <span className="ap-file cad">
          <i />
          {t('deck.pyramidFileDrawing')}
        </span>
      </div>
      <div className="ap-flow" />
      <div className="ap-proc">
        {t('deck.pyramidProcLine1')}
        <br />
        {t('deck.pyramidProcLine2')}
      </div>
      <div className="ap-flow2" />
      <div className="ap-label" style={{ right: '5%', top: '6%' }}>
        {t('deck.pyramidLabelPyramid')}
      </div>
      <div className="ap-right">
      <div className="ap-query">{t('deck.pyramidQuery')}</div>
      <div className="ap-pyramid">
        <div className="ap-tier">
          <div className="ap-row">
            <span className="ap-block l0 ap-top hit0">{t('deck.pyramidTop')}</span>
          </div>
          <div className="ap-tier-note">
            <b>{t('deck.pyramidLevel0Title')}</b>
            <span>{t('deck.pyramidLevel0Desc')}</span>
          </div>
        </div>
        <div className="ap-tier">
          <div className="ap-row">
            <span className="ap-block l1">{t('deck.pyramidChapterA')}</span>
            <span className="ap-block l1 hit1">{t('deck.pyramidChapterB')}</span>
            <span className="ap-block l1">{t('deck.pyramidChapterC')}</span>
          </div>
          <div className="ap-tier-note">
            <b>{t('deck.pyramidLevel1Title')}</b>
            <span>{t('deck.pyramidLevel1Desc')}</span>
          </div>
        </div>
        <div className="ap-tier">
          <div className="ap-row">
            <span className="ap-block l2">&nbsp;</span>
            <span className="ap-block l2">&nbsp;</span>
            <span className="ap-block l2 hit2">&nbsp;</span>
            <span className="ap-block l2 hit2">&nbsp;</span>
            <span className="ap-block l2">&nbsp;</span>
            <span className="ap-block l2">&nbsp;</span>
          </div>
          <div className="ap-tier-note">
            <b>{t('deck.pyramidLevel2Title')}</b>
            <span>{t('deck.pyramidLevel2Desc')}</span>
          </div>
        </div>
      </div>
      <div className="ap-result">
        <span>&#10003;</span> {t('deck.pyramidResult')}
      </div>
      </div>

      {/* Legende: erklärt den hervorgehobenen Pfad von Überblick bis zur exakten Passage */}
      <div className="ap-legend">
        <span className="ap-legend-title">{t('deck.pyramidLegendTitle')}</span>
        <span className="ap-legend-item">
          <i className="ap-dot ap-dot-q" />
          {t('deck.pyramidLegendQuestion')}
        </span>
        <span className="ap-legend-item">
          <i className="ap-dot ap-dot-path" />
          {t('deck.pyramidLegendPath')}
        </span>
        <span className="ap-legend-item">
          <i className="ap-dot ap-dot-hit" />
          {t('deck.pyramidLegendHit')}
        </span>
      </div>
    </div>
  );
}

/** Antwort-Pipeline (14s Loop). */
function Pipeline() {
  const { t } = useTranslation('demo');
  return (
    <div className="anim-stage anim-pipeline" aria-label={t('deck.pipelineAriaLabel')}>
      <div className="pl-track">
        <div className="pl-step">
          <span className="num">1</span>
          <h4>{t('deck.pipelineStep1Title')}</h4>
          <p>{t('deck.pipelineStep1Body')}</p>
          <div className="pl-pulse" />
        </div>
        <div className="pl-step">
          <span className="num">2</span>
          <h4>{t('deck.pipelineStep2Title')}</h4>
          <p>{t('deck.pipelineStep2Body')}</p>
          <div className="pl-lanes">
            <div className="pl-lane sem" />
          </div>
        </div>
        <div className="pl-step">
          <span className="num">3</span>
          <h4>{t('deck.pipelineStep3Title')}</h4>
          <p>{t('deck.pipelineStep3Body')}</p>
          <div className="pl-lanes">
            <div className="pl-lane sem" />
            <div className="pl-lane kw" />
          </div>
        </div>
        <div className="pl-step">
          <span className="num">4</span>
          <h4>{t('deck.pipelineStep4Title')}</h4>
          <p>{t('deck.pipelineStep4Body')}</p>
          <div className="pl-bars">
            <div className="pl-bar" />
            <div className="pl-bar" />
            <div className="pl-bar" />
          </div>
        </div>
        <div className="pl-step">
          <span className="num">5</span>
          <h4>{t('deck.pipelineStep5Title')}</h4>
          <p>{t('deck.pipelineStep5Body')}</p>
          <div className="pl-check">
            <i>&#10003;</i> {t('deck.pipelineStep5Check')}
          </div>
        </div>
      </div>
    </div>
  );
}

export function PlatformShowcase() {
  const { t } = useTranslation('home');
  return (
    <section
      id="how-it-works"
      className="bnk-deck"
      aria-labelledby="bnk-showcase-heading"
      style={{
        width: '100%',
        padding: 'clamp(56px, 8vw, 96px) clamp(20px, 5vw, 72px)',
      }}
    >
      <header style={{ maxWidth: '80ch', marginBottom: 'clamp(28px, 4vw, 52px)' }}>
        <h2
          id="bnk-showcase-heading"
          style={{
            color: 'var(--bnk-text)',
            fontSize: 'clamp(26px, 3vw, 44px)',
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
            margin: 0,
          }}
        >
          {t('platformShowcase.heading')}
        </h2>
        <p
          style={{
            color: 'var(--bnk-muted)',
            fontSize: 'clamp(15px, 1.3vw, 19px)',
            lineHeight: 1.5,
            marginTop: '14px',
          }}
        >
          {t('platformShowcase.intro')}
        </p>
      </header>

      <div className="bnk-stage-grid">
        <figure className="bnk-stage bnk-stage-pyramid" style={{ margin: 0 }}>
          <KnowledgePyramid />
          <figcaption className="bnk-stage-caption">{t('platformShowcase.captionPyramid')}</figcaption>
        </figure>

        <figure className="bnk-stage bnk-stage-pipeline" style={{ margin: 0 }}>
          <h3 className="bnk-stage-heading">{t('platformShowcase.pipelineHeading')}</h3>
          <Pipeline />
          <figcaption className="bnk-stage-caption">{t('platformShowcase.captionPipeline')}</figcaption>
        </figure>
      </div>
    </section>
  );
}
